import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";

type ColorBlob = {
  color: string;
  left: string;
  top: string;
  width: string;
  height: string;
  opacity: number;
};

type GlowProps = React.PropsWithChildren<{
  className?: string;
  /**
   * Border radius to apply to the glow wrapper and content.
   * Use Tailwind radius classes (e.g., `rounded-xl`).
   */
  roundedClassName?: string;
  /**
   * Extra classes for tuning intensity/size of the glow layer.
   * Example: "blur-2xl opacity-70 -inset-1".
   */
  glowClassName?: string;
}>;

/**
 * NOTE (Kevin, 2025-08-17): Color blob configuration for watercolor-style multicolor glow.
 * Positions arranged clockwise from top left corner.
 */
const COLOR_BLOBS: ColorBlob[] = [
  // Light green - top left corner
  {
    color: "#B7FEAE",
    left: "-1%",
    top: "6%",
    width: "25%",
    height: "10%",
    opacity: 1,
  },
  // Teal - top left-center
  {
    color: "#03AB8C",
    left: "20%",
    top: "6%",
    width: "22%",
    height: "6%",
    opacity: 0.9,
  },
  // Blue - top center
  {
    color: "#274AE2",
    left: "40%",
    top: "6%",
    width: "20%",
    height: "6%",
    opacity: 0.9,
  },
  // Purple - top right-center
  {
    color: "#8B32A8",
    left: "60%",
    top: "6%",
    width: "25%",
    height: "6%",
    opacity: 0.9,
  },
  // Pink - top right
  {
    color: "#FFB3C3",
    left: "75%",
    top: "6%",
    width: "18%",
    height: "6%",
    opacity: 1,
  },
  // Yellow - top right corner
  {
    color: "#FFEB80",
    left: "82%",
    top: "6%",
    width: "22%",
    height: "6%",
    opacity: 1,
  },

  // Purple - right side upper
  {
    color: "#8B32A8",
    left: "75%",
    top: "30%",
    width: "18%",
    height: "8%",
    opacity: 0.7,
  },
  // Pink - right side center-upper
  {
    color: "#FFB3C3",
    left: "84%",
    top: "40%",
    width: "20%",
    height: "6%",
    opacity: 0.9,
  },
  // Blue - right side center-lower
  {
    color: "#274AE2",
    left: "75%",
    top: "50%",
    width: "18%",
    height: "8%",
    opacity: 0.7,
  },
  // Purple - right side lower
  {
    color: "#8B32A8",
    left: "82%",
    top: "70%",
    width: "20%",
    height: "8%",
    opacity: 0.7,
  },

  // Pink - bottom right corner
  {
    color: "#FFB3C3",
    left: "80%",
    top: "100%",
    width: "22%",
    height: "10%",
    opacity: 1,
  },
  // Purple - bottom right-center
  {
    color: "#8B32A8",
    left: "60%",
    top: "105%",
    width: "20%",
    height: "10%",
    opacity: 0.9,
  },
  // Blue - bottom center
  {
    color: "#274AE2",
    left: "45%",
    top: "104%",
    width: "20%",
    height: "10%",
    opacity: 0.9,
  },
  // Teal - bottom left-center
  {
    color: "#03AB8C",
    left: "20%",
    top: "108%",
    width: "22%",
    height: "8%",
    opacity: 0.9,
  },
  // Light green - bottom left
  {
    color: "#B7FEAE",
    left: "10%",
    top: "108%",
    width: "20%",
    height: "8%",
    opacity: 1,
  },
  // Yellow - bottom left corner
  {
    color: "#FFEB80",
    left: "0%",
    top: "108%",
    width: "25%",
    height: "10%",
    opacity: 1,
  },
  // Pink - bottom left side
  {
    color: "#FFB3C3",
    left: "-3%",
    top: "88%",
    width: "20%",
    height: "8%",
    opacity: 1,
  },

  // Pink - left side lower
  {
    color: "#FFB3C3",
    left: "-2%",
    top: "70%",
    width: "22%",
    height: "10%",
    opacity: 0.9,
  },
  // Purple - left side center
  {
    color: "#8B32A8",
    left: "-2%",
    top: "50%",
    width: "20%",
    height: "8%",
    opacity: 0.7,
  },
  // Blue - left side upper
  {
    color: "#274AE2",
    left: "-2%",
    top: "30%",
    width: "22%",
    height: "10%",
    opacity: 0.7,
  },
];

/**
 * Watercolor-style multicolor glow built from individual blurred ellipses.
 */
export default function Glow({
  children,
  className,
  roundedClassName,
  glowClassName,
}: GlowProps) {
  return (
    <div className={twMerge("relative", roundedClassName, className)}>
      <div
        className={twMerge(
          "pointer-events-none absolute -inset-1",
          roundedClassName,
          glowClassName,
        )}
        style={{ filter: "blur(25px)" }}
      >
        {COLOR_BLOBS.map((blob, index) => (
          <div
            key={index}
            className="absolute rounded-sm"
            style={{
              backgroundColor: blob.color,
              left: blob.left,
              top: blob.top,
              width: blob.width,
              height: blob.height,
              opacity: blob.opacity,
            }}
          />
        ))}
      </div>
      <div className={twMerge("relative", roundedClassName)}>{children}</div>
    </div>
  );
}
